import React, { PropsWithChildren, TextareaHTMLAttributes, forwardRef } from 'react'
import { UseFormRegister } from 'react-hook-form'

import clsx from 'clsx'
import FieldError, { FIELD_INVALID, FieldLabel } from '../input/FieldParts'

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string
  wrapperClassName?: string
  label?: string
  name: string
  placeholder?: string
  error?: any
  register?: UseFormRegister<any>
}

const TextAreaField = forwardRef<HTMLTextAreaElement, PropsWithChildren<TextAreaFieldProps>>(
  (
    {
      className,
      wrapperClassName,
      label,
      placeholder,
      name,
      error,
      register,
      children,
      'aria-describedby': describedBy,
      ...rest
    },
    ref,
  ) => {
    // the message under the field belongs to it (read with the field)
    const errorId = error?.message ? `${name}-error` : undefined
    const describedByIds = [describedBy, errorId].filter(Boolean).join(' ') || undefined
    return (
      <div className={clsx('flex flex-col relative', wrapperClassName)}>
        {label && <FieldLabel className="mb-1.5" label={label} htmlFor={name} />}
        {/* `border`: the colour alone drew nothing (Tailwind resets border
            width), so on white cards the field was invisible. Usages that
            want no frame pass !border-0. */}
        <textarea
          ref={ref}
          className={clsx(
            // 16px on phones like every field (iOS zooms into a smaller one)
            'w-full h-[5.9375rem] min-h-[5.9375rem] max-h-[60vh] px-3.5 py-3 bg-surface rounded-xl text-ink placeholder:text-ink-3 border border-ink/[0.18] border-solid text-base md:text-[0.9375rem] font-normal leading-[1.375rem] transition-colors duration-200 hover:border-ink/30 focus:outline-none focus:border-brand focus:shadow-[0_0_0_0.1875rem_rgba(83,112,246,0.2)] resize-y overflow-y-auto',
            error && FIELD_INVALID,
            className,
          )}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds}
          id={name}
          {...(register && register(name))}
          {...rest}
        >
          {children}
        </textarea>
        {error?.message && <FieldError id={errorId} message={error.message} />}
      </div>
    )
  },
)

TextAreaField.displayName = 'TextAreaField'

export default TextAreaField
